import { __extends } from "tslib";
import { NodeMaterialBlock } from "../../nodeMaterialBlock.js";
import { NodeMaterialBlockConnectionPointTypes } from "../../Enums/nodeMaterialBlockConnectionPointTypes.js";
import { NodeMaterialSystemValues } from "../../Enums/nodeMaterialSystemValues.js";
import { NodeMaterialBlockTargets } from "../../Enums/nodeMaterialBlockTargets.js";
import { MaterialHelper } from "../../../materialHelper.js";
import { InputBlock } from "../Input/inputBlock.js";
import { RegisterClass } from "../../../../Misc/typeStore.js";
import "../../../../Shaders/ShadersInclude/bonesDeclaration.js";
import "../../../../Shaders/ShadersInclude/bonesVertex.js";
/**
 * Block used to add support for vertex skinning (bones)
 */
var BonesBlock = /** @class */ (function (_super) {
    __extends(BonesBlock, _super);
    /**
     * Creates a new BonesBlock
     * @param name defines the block name
     */
    function BonesBlock(name) {
        var _this = _super.call(this, name, NodeMaterialBlockTargets.Vertex) || this;
        _this.registerInput("matricesIndices", NodeMaterialBlockConnectionPointTypes.Vector4);
        _this.registerInput("matricesWeights", NodeMaterialBlockConnectionPointTypes.Vector4);
        _this.registerInput("matricesIndicesExtra", NodeMaterialBlockConnectionPointTypes.Vector4, true);
        _this.registerInput("matricesWeightsExtra", NodeMaterialBlockConnectionPointTypes.Vector4, true);
        _this.registerInput("world", NodeMaterialBlockConnectionPointTypes.Matrix);
        _this.registerOutput("output", NodeMaterialBlockConnectionPointTypes.Matrix);
        return _this;
    }
    /**
     * Initialize the block and prepare the context for build
     * @param state defines the state that will be used for the build
     */
    BonesBlock.prototype.initialize = function (state) {
        state._excludeVariableName("boneSampler");
        state._excludeVariableName("boneTextureWidth");
        state._excludeVariableName("mBones");
        state._excludeVariableName("BonesPerMesh");
    };
    /**
     * Gets the current class name
     * @returns the class name
     */
    BonesBlock.prototype.getClassName = function () {
        return "BonesBlock";
    };
    Object.defineProperty(BonesBlock.prototype, "matricesIndices", {
        /**
         * Gets the matrix indices input component
         */
        get: function () {
            return this._inputs[0];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(BonesBlock.prototype, "matricesWeights", {
        /**
         * Gets the matrix weights input component
         */
        get: function () {
            return this._inputs[1];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(BonesBlock.prototype, "matricesIndicesExtra", {
        /**
         * Gets the extra matrix indices input component
         */
        get: function () {
            return this._inputs[2];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(BonesBlock.prototype, "matricesWeightsExtra", {
        /**
         * Gets the extra matrix weights input component
         */
        get: function () {
            return this._inputs[3];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(BonesBlock.prototype, "world", {
        /**
         * Gets the world input component
         */
        get: function () {
            return this._inputs[4];
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(BonesBlock.prototype, "output", {
        /**
         * Gets the output component
         */
        get: function () {
            return this._outputs[0];
        },
        enumerable: false,
        configurable: true
    });
    BonesBlock.prototype.autoConfigure = function (material) {
        if (!this.matricesIndices.isConnected) {
            var matricesIndicesInput = material.getInputBlockByPredicate(function (b) { return b.isAttribute && b.name === "matricesIndices"; });
            if (!matricesIndicesInput) {
                matricesIndicesInput = new InputBlock("matricesIndices");
                matricesIndicesInput.setAsAttribute("matricesIndices");
            }
            matricesIndicesInput.output.connectTo(this.matricesIndices);
        }
        if (!this.matricesWeights.isConnected) {
            var matricesWeightsInput = material.getInputBlockByPredicate(function (b) { return b.isAttribute && b.name === "matricesWeights"; });
            if (!matricesWeightsInput) {
                matricesWeightsInput = new InputBlock("matricesWeights");
                matricesWeightsInput.setAsAttribute("matricesWeights");
            }
            matricesWeightsInput.output.connectTo(this.matricesWeights);
        }
        if (!this.world.isConnected) {
            var worldInput = material.getInputBlockByPredicate(function (b) { return b.systemValue === NodeMaterialSystemValues.World; });
            if (!worldInput) {
                worldInput = new InputBlock("world");
                worldInput.setAsSystemValue(NodeMaterialSystemValues.World);
            }
            worldInput.output.connectTo(this.world);
        }
    };
    BonesBlock.prototype.provideFallbacks = function (mesh, fallbacks) {
        if (mesh && mesh.useBones && mesh.computeBonesUsingShaders && mesh.skeleton) {
            fallbacks.addCPUSkinningFallback(0, mesh);
        }
    };
    BonesBlock.prototype.bind = function (effect, nodeMaterial, mesh) {
        MaterialHelper.BindBonesParameters(mesh, effect);
    };
    BonesBlock.prototype.prepareDefines = function (mesh, nodeMaterial, defines) {
        if (!defines._areAttributesDirty) {
            return;
        }
        MaterialHelper.PrepareDefinesForBones(mesh, defines);
    };
    BonesBlock.prototype._buildBlock = function (state) {
        _super.prototype._buildBlock.call(this, state);
        // Register for compilation fallbacks
        state.sharedData.blocksWithFallbacks.push(this);
        // Register for binding
        state.sharedData.forcedBindableBlocks.push(this);
        // Register for defines
        state.sharedData.blocksWithDefines.push(this);
        // Register internal uniforms and samplers
        state.uniforms.push("boneTextureWidth");
        state.uniforms.push("mBones");
        state.samplers.push("boneSampler");
        // Emit code
        var comments = "//".concat(this.name);
        state._emitFunctionFromInclude("bonesDeclaration", comments, {
            removeAttributes: true,
            removeUniforms: false,
            removeVaryings: true,
            removeIfDef: false,
        });
        var influenceVariablename = state._getFreeVariableName("influence");
        state.compilationString += state._emitCodeFromInclude("bonesVertex", comments, {
            replaceStrings: [
                {
                    search: /finalWorld=finalWorld\*influence;/,
                    replace: "",
                },
                {
                    search: /influence/gm,
                    replace: influenceVariablename,
                },
            ],
        });
        var output = this._outputs[0];
        var worldInput = this.world;
        state.compilationString += "#if NUM_BONE_INFLUENCERS>0\r\n";
        state.compilationString += this._declareOutput(output, state) + " = ".concat(worldInput.associatedVariableName, " * ").concat(influenceVariablename, ";\r\n");
        state.compilationString += "#else\r\n";
        state.compilationString += this._declareOutput(output, state) + " = ".concat(worldInput.associatedVariableName, ";\r\n");
        state.compilationString += "#endif\r\n";
        return this;
    };
    return BonesBlock;
}(NodeMaterialBlock));
export { BonesBlock };
RegisterClass("BABYLON.BonesBlock", BonesBlock);
//# sourceMappingURL=bonesBlock.js.map